/**
 * Artifact Generation Panel (formerly Artifact Design)
 *
 * PROJECT-LEVEL ONLY: Displays AI-analyzed artifact proposals from master plan
 * User can select, edit, and generate artifacts which then become project artifacts
 *
 * REDESIGNED: Row-based UI with collapsible sections (similar to Explorer)
 */

import { useMemo, useState, useCallback } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { buildUserHeaders } from '@/stores/userStore';
import { Button } from '../ui/Button';
import { Loader2 } from 'lucide-react';
import { ArtifactSelectionList, type ArtifactGroup } from '../ui/ArtifactSelectionList';
import type { ArtifactDesign, Artifact } from '@/types/artifactDesign';
import { normalizeArtifactDesign } from '@/utils/artifactDesign';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { API_BASE_URL } from '@/lib/config';

export const ArtifactDesignPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const projects = useProjectStore((state) => state.projects);
  const updateProjectStage = useProjectStore((state) => state.updateProjectStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const { addToast } = useToastStore();

  const [generating, setGenerating] = useState(false);
  const [generatingPreviews, setGeneratingPreviews] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [pendingGenerationIds, setPendingGenerationIds] = useState<string[] | null>(null);

  // Get current project
  const project = activeProjectId ? projects[activeProjectId] : null;

  // Get ARTIFACT_DESIGN stage content from PROJECT level (not artifact level)
  const artifactDesignStage = project?.stages.ARTIFACT_DESIGN;
  const rawArtifactDesign = artifactDesignStage?.content
    ? JSON.parse(artifactDesignStage.content)
    : null;

  const artifactDesign: ArtifactDesign | null = rawArtifactDesign
    ? normalizeArtifactDesign(rawArtifactDesign, project?.suffix ?? rawArtifactDesign.projectSuffix ?? '')
    : null;

  /**
   * Phase 6.2: Generate Previews with LLM
   */
  const handleGeneratePreviews = async () => {
    if (!activeProjectId || !artifactDesign) return;

    setGeneratingPreviews(true);
    try {
      const response = await fetch(`http://localhost:50677/api/projects/${activeProjectId}/artifact-design/preview`, {
        method: 'POST',
        headers: buildUserHeaders({ 'Content-Type': 'application/json' })
      });

      if (!response.ok) {
        throw new Error('Failed to generate previews');
      }

      const data = await response.json();

      if (data.enrichedDesign) {
        updateProjectStage(activeProjectId, 'ARTIFACT_DESIGN', JSON.stringify(data.enrichedDesign), false);
      }

      addToast('success', 'Generated artifact previews with AI');
    } catch (error: any) {
      console.error('Error generating previews:', error);
      addToast('error', `Failed to generate previews: ${error.message || 'Unknown error'}`);
    } finally {
      setGeneratingPreviews(false);
    }
  };

  /**
   * Generate selected artifacts
   */
  const existingArtifactsCount = useMemo(
    () => (project?.artifacts ? Object.keys(project.artifacts).length : 0),
    [project]
  );

  const runGeneration = useCallback(async (selectedArtifactIds: string[]) => {
    if (!artifactDesign || !activeProjectId || !project) return;

    setGenerating(true);
    try {
      const projectArtifactId = project.id;

      const response = await fetch(`${API_BASE_URL}/transforms/artifact-design-to-prompts`, {
        method: 'POST',
        headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          projectId: activeProjectId,
          artifactId: projectArtifactId,
          selectedArtifactIds,
          deleteExisting: existingArtifactsCount > 0
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to generate artifact prompts');
      }

      const result = await response.json();

      // Refresh project artifacts so Explorer reflects new records
      await useProjectStore.getState().loadArtifacts(activeProjectId);

      if (Array.isArray(result.artifacts) && result.artifacts.length > 0) {
        const firstArtifactId = result.artifacts[0].artifactId;
        if (firstArtifactId) {
          useProjectStore.getState().setSelectedArtifact(firstArtifactId);
          setActiveStage('PROMPT');
        }
      }

      setGenerating(false);
      addToast('success', `Created ${result.count} artifact(s) with PROMPT stage. Click each to continue refining.`);
    } catch (error: any) {
      console.error('Generation error:', error);
      addToast('error', `Failed to create artifacts: ${error.message || 'Unknown error'}`);
      setGenerating(false);
    }
  }, [artifactDesign, activeProjectId, project, existingArtifactsCount, addToast]);

  const handleGenerateSelected = () => {
    if (!artifactDesign || !activeProjectId || !project) return;

    // Get ALL selected artifacts across all types
    const selectedSkills = artifactDesign.artifacts.skills?.items?.filter(s => s.selected) || [];
    const selectedSubskills = artifactDesign.artifacts.subskills?.items?.filter(s => s.selected) || [];
    const selectedSubflows = artifactDesign.artifacts.subflows?.items?.filter(s => s.selected) || [];
    const selectedAttributes = artifactDesign.artifacts.attributes?.items?.filter(a => a.selected) || [];
    const selectedIntegrations = artifactDesign.artifacts.integrations?.items?.filter(i => i.selected) || [];
    const selectedFacets = artifactDesign.artifacts.facets?.items?.filter(f => f.selected) || [];

    const allSelected: Artifact[] = [
      ...selectedSkills,
      ...selectedSubskills,
      ...selectedSubflows,
      ...selectedAttributes,
      ...selectedIntegrations,
      ...selectedFacets
    ];

    if (allSelected.length === 0) {
      addToast('warning', 'No artifacts selected');
      return;
    }

    const selectedArtifactIds = allSelected.map(a => a.id);

    if (existingArtifactsCount > 0) {
      setPendingGenerationIds(selectedArtifactIds);
      setShowRegenerateConfirm(true);
      return;
    }

    runGeneration(selectedArtifactIds);
  };

  // Handle selection changes
  const handleSelectionChange = (artifactId: string, artifactType: 'skill' | 'subskill' | 'subflow' | 'attribute' | 'integration' | 'facet', selected: boolean) => {
    if (!artifactDesign || !activeProjectId) return;

    const updated = { ...artifactDesign };

    // Toggle selection for the specific artifact
    const typeKey = artifactType === 'skill' ? 'skills'
      : artifactType === 'subskill' ? 'subskills'
      : artifactType === 'subflow' ? 'subflows'
      : artifactType === 'attribute' ? 'attributes'
      : artifactType === 'integration' ? 'integrations'
      : 'facets';

    updated.artifacts[typeKey] = {
      ...updated.artifacts[typeKey],
      items: updated.artifacts[typeKey]?.items?.map(item =>
        item.id === artifactId ? { ...item, selected } : item
      ) || []
    };

    updateProjectStage(activeProjectId, 'ARTIFACT_DESIGN', JSON.stringify(updated), true);
  };

  // Handle type toggle (select/deselect all of a type)
  const handleTypeToggle = (artifactType: 'skill' | 'subskill' | 'subflow' | 'attribute' | 'integration' | 'facet', selected: boolean) => {
    if (!artifactDesign || !activeProjectId) return;

    const updated = { ...artifactDesign };

    const typeKey = artifactType === 'skill' ? 'skills'
      : artifactType === 'subskill' ? 'subskills'
      : artifactType === 'subflow' ? 'subflows'
      : artifactType === 'attribute' ? 'attributes'
      : artifactType === 'integration' ? 'integrations'
      : 'facets';

    updated.artifacts[typeKey] = {
      ...updated.artifacts[typeKey],
      items: updated.artifacts[typeKey]?.items?.map(item => ({ ...item, selected })) || []
    };

    updateProjectStage(activeProjectId, 'ARTIFACT_DESIGN', JSON.stringify(updated), true);
  };

  const handleSelectAll = (selected: boolean) => {
    if (!artifactDesign || !activeProjectId) return;

    const updated = { ...artifactDesign, artifacts: { ...artifactDesign.artifacts } } as ArtifactDesign;

    (['skills', 'subskills', 'subflows', 'attributes', 'integrations', 'facets'] as const).forEach((key) => {
      const items = updated.artifacts[key]?.items;
      if (!items) return;
      (updated.artifacts as any)[key] = {
        items: items.map((item) => ({
          ...item,
          selected
        }))
      };
    });

    updateProjectStage(activeProjectId, 'ARTIFACT_DESIGN', JSON.stringify(updated), true);
  };

  const groups: ArtifactGroup[] = useMemo(() => {
    const skills = artifactDesign?.artifacts.skills?.items || [];
    const subskills = artifactDesign?.artifacts.subskills?.items || [];
    const subflows = artifactDesign?.artifacts.subflows?.items || [];
    const attributes = artifactDesign?.artifacts.attributes?.items || [];
    const integrations = artifactDesign?.artifacts.integrations?.items || [];
    const facets = artifactDesign?.artifacts.facets?.items || [];

    return [
      { type: 'skill', artifacts: skills },
      { type: 'subskill', artifacts: subskills },
      { type: 'subflow', artifacts: subflows },
      { type: 'attribute', artifacts: attributes },
      { type: 'integration', artifacts: integrations },
      { type: 'facet', artifacts: facets }
    ];
  }, [artifactDesign]);

  const totalSelected = useMemo(
    () => groups.reduce((sum, g) => sum + g.artifacts.filter(a => a.selected).length, 0),
    [groups]
  );

  if (!project) {
    return (
      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
        <p className="text-sm">No project selected.</p>
      </div>
    );
  }

  if (!artifactDesign) {
    return (
      <div className="w-full h-full flex flex-col">
        {/* Empty State */}
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <svg
                className="w-8 h-8 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">No Artifact Generation Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Use the "To Artifact Design" button in the BPMN Preview tab to analyze your project LumaScript and generate artifact proposals.
            </p>
            <Button onClick={() => setActiveStage('BPMN')}>
              Go to Preview
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Bar with Project Context */}
      <div className="h-10 border-b border-border bg-background flex items-center justify-between px-4 flex-shrink-0">
        {/* Left: Project Name */}
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-foreground">
            {project.name}
          </span>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {totalSelected} selected
          </span>

          {generatingPreviews ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Generating previews...</span>
            </div>
          ) : (
            <button
              onClick={handleGeneratePreviews}
              disabled={generating || generatingPreviews}
              className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              [Generate Previews]
            </button>
          )}

          {generating ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Generating...</span>
            </div>
          ) : (
            <button
              onClick={handleGenerateSelected}
              disabled={totalSelected === 0 || generating}
              className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              [Generate Artifact Prompts ({totalSelected})]
            </button>
          )}
        </div>
      </div>

      {/* Artifact Selection List */}
      <div className="flex-1 overflow-auto">
        <ArtifactSelectionList
          groups={groups}
          onSelectionChange={handleSelectionChange}
          onTypeToggle={handleTypeToggle}
          onSelectAll={handleSelectAll}
          mode="generation"
        />
      </div>

      <ConfirmDialog
        isOpen={showRegenerateConfirm}
        title="Overwrite Existing Artifacts?"
        message={`This project already has ${existingArtifactsCount} artifact(s). Regenerating will delete them and create new PROMPT stages. Continue?`}
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
        onConfirm={() => {
          setShowRegenerateConfirm(false);
          if (pendingGenerationIds) {
            runGeneration(pendingGenerationIds);
          }
          setPendingGenerationIds(null);
        }}
        onCancel={() => {
          setShowRegenerateConfirm(false);
          setPendingGenerationIds(null);
        }}
      />
    </div>
  );
};
